import api from "./api";

/**
 * The people this business serves — BIZ-15 and BIZ-16.
 *
 * Its own records, not the platform's app accounts: most people a business
 * books never open the customer app. `is_registered` says which is which.
 *
 * ## The one cursor-paginated list in the portal
 *
 * Every other list here pages by number. This one does not, because the API
 * does not: the client list is the only list large enough for `OFFSET` to hurt,
 * and a cursor stays anchored to a row while colleagues are adding clients.
 * The cost is that a cursor cannot count, so `with_total` is asked for
 * separately and only when the count is actually wanted.
 */

/** Filters -> query string, shared by the list and the export so they cannot drift. */
const toParams = (filters = {}) => {
  const params = {};

  if (filters.search) params.search = filters.search;
  if (filters.tags?.length) params.tags = filters.tags.join(",");
  if (filters.branch) params.branch = filters.branch;
  if (filters.notVisitedSince) params.not_visited_since = filters.notVisitedSince;

  if (filters.consent) {
    params.consent = filters.consent;
    // Only meaningful alongside a purpose; the API defaults it to true.
    params.consent_granted = filters.consentGranted === "no" ? 0 : 1;
  }

  // Values (and the value sort) are in this currency only; "" = the main one.
  if (filters.currency) params.currency = filters.currency;

  // false is a real filter ("has no app account"), so "" is the only "any".
  if (filters.registered !== "" && filters.registered != null) {
    params.registered = filters.registered;
  }

  return params;
};

/**
 * GET /business/clients
 *
 * `cursor` is the opaque `next_cursor` / `prev_cursor` from a previous page's
 * meta — never a page number. `withTotal` runs the extra count.
 */
export const fetchClients = async ({
  cursor = null,
  perPage = 25,
  filters = {},
  sort = "name",
  direction = "asc",
  withTotal = false,
} = {}) => {
  const params = { ...toParams(filters), per_page: perPage, sort, direction };

  if (cursor) params.cursor = cursor;
  if (withTotal) params.with_total = 1;

  const { data } = await api.get("/business/clients", { params });
  return { clients: data.data.clients, meta: data.meta };
};

/** GET /business/clients/{id} — carries tags and the standing consent position. */
export const fetchClient = async (id) => {
  const { data } = await api.get(`/business/clients/${id}`);
  return data.data.client;
};

/**
 * Form state -> the payload the API expects.
 *
 * Only the keys the form actually offered are sent. The API writes exactly what
 * it receives, so an absent key leaves that column alone — which is what keeps
 * a tag edit from wiping a phone number.
 */
const toPayload = (values) => {
  const payload = { name: values.name };

  if (values.phone !== undefined) payload.phone = values.phone || null;
  if (values.email !== undefined) payload.email = values.email || null;
  if (values.reference !== undefined) payload.reference = values.reference || null;
  if (values.preferredLanguage !== undefined) {
    payload.preferred_language = values.preferredLanguage || null;
  }
  if (values.preferences !== undefined) payload.preferences = values.preferences;
  if (values.customerId !== undefined) payload.customer_id = values.customerId || null;
  // Absent means "leave the tags alone"; an empty array clears them.
  if (values.tagIds !== undefined) payload.tags = values.tagIds;

  return payload;
};

/** POST /business/clients */
export const createClient = async (values) => {
  const { data } = await api.post("/business/clients", toPayload(values));
  return data.data.client;
};

/** PUT /business/clients/{id} */
export const updateClient = async (id, values) => {
  const { data } = await api.put(`/business/clients/${id}`, toPayload(values));
  return data.data.client;
};

/** DELETE /business/clients/{id} — soft delete; their history is kept. */
export const deleteClient = (id) => api.delete(`/business/clients/${id}`);

/* ------------------------------------------------------------------- export */

/** The filename the API named, or a sensible fallback. */
const filenameFrom = (disposition, fallback) => {
  const match = /filename="?([^"]+)"?/.exec(disposition ?? "");
  return match ? match[1] : fallback;
};

/**
 * GET /business/clients/export — the filtered list as a CSV.
 *
 * The response is a stream behind an Authorization header, so it cannot be a
 * plain link: it is fetched as a blob and handed to the browser as a download.
 * The object URL is revoked straight after, or the blob is held until the tab
 * closes.
 */
export const exportClients = async (filters = {}) => {
  const response = await api.get("/business/clients/export", {
    params: toParams(filters),
    responseType: "blob",
  });

  const name = filenameFrom(
    response.headers["content-disposition"],
    `clients-${new Date().toISOString().slice(0, 10)}.csv`
  );

  const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return name;
};

/* --------------------------------------------------------------------- tags */

/** GET /business/client-tags — every label this business uses, with its count. */
export const fetchClientTags = async () => {
  const { data } = await api.get("/business/client-tags");
  return data.data.tags;
};

/** POST /business/client-tags */
export const createClientTag = async ({ name, colour }) => {
  const { data } = await api.post("/business/client-tags", { name, colour });
  return data.data.tag;
};

/** PUT /business/client-tags/{id} */
export const updateClientTag = async (id, { name, colour }) => {
  const { data } = await api.put(`/business/client-tags/${id}`, { name, colour });
  return data.data.tag;
};

/** DELETE /business/client-tags/{id} — the label goes off every client wearing it. */
export const deleteClientTag = (id) => api.delete(`/business/client-tags/${id}`);

/* ----------------------------------------------------------------- timeline */

/**
 * GET /business/clients/{id}/timeline
 *
 * `meta.sources` names what the history is built from — bookings today, and
 * payments once file 10 lands. The screen reads that list rather than assuming.
 */
export const fetchClientTimeline = async (id, { page = 1, perPage = 15, status = "" } = {}) => {
  const params = { page, per_page: perPage };
  if (status) params.status = status;

  const { data } = await api.get(`/business/clients/${id}/timeline`, { params });
  return { appointments: data.data.appointments, meta: data.meta };
};

/* -------------------------------------------------------------------- notes */

/**
 * GET /business/clients/{id}/notes
 *
 * Visibility is enforced in the API's query, so a note this user may not read
 * never arrives here — there is nothing for the screen to hide.
 */
export const fetchClientNotes = async (id, { page = 1, perPage = 15 } = {}) => {
  const { data } = await api.get(`/business/clients/${id}/notes`, {
    params: { page, per_page: perPage },
  });
  return { notes: data.data.notes, meta: data.meta };
};

/** POST /business/clients/{id}/notes */
export const createClientNote = async (id, { body, visibility }) => {
  const { data } = await api.post(`/business/clients/${id}/notes`, { body, visibility });
  return data.data.note;
};

/** PUT — the old body is filed as a revision first; nothing is overwritten. */
export const updateClientNote = async (clientId, noteId, { body, visibility }) => {
  const payload = { body };
  if (visibility) payload.visibility = visibility;

  const { data } = await api.put(`/business/clients/${clientId}/notes/${noteId}`, payload);
  return data.data.note;
};

/** DELETE /business/clients/{id}/notes/{noteId} */
export const deleteClientNote = (clientId, noteId) =>
  api.delete(`/business/clients/${clientId}/notes/${noteId}`);

/** GET the earlier versions of one note — what makes "no silent overwrite" checkable. */
export const fetchNoteRevisions = async (clientId, noteId) => {
  const { data } = await api.get(`/business/clients/${clientId}/notes/${noteId}/revisions`);
  return data.data.revisions;
};

/* ------------------------------------------------------------------ consent */

/**
 * GET /business/clients/{id}/consents — every receipt, plus where they stand now.
 *
 * `current` is the standing position: the latest receipt per purpose and
 * channel. No receipt at all means no, because consent is given, not assumed.
 */
export const fetchClientConsents = async (id, { purpose = "" } = {}) => {
  const params = {};
  if (purpose) params.purpose = purpose;

  const { data } = await api.get(`/business/clients/${id}/consents`, { params });
  return { consents: data.data.consents, current: data.data.current };
};

/**
 * POST a receipt. Withdrawing is `granted: false` — a new receipt, never an
 * edit of the one that granted it, because evidence you can edit is not
 * evidence.
 */
export const recordClientConsent = async (id, values) => {
  const payload = {
    purpose: values.purpose,
    channel: values.channel,
    granted: Boolean(values.granted),
    source: values.source || "portal",
  };

  if (values.version) payload.version = values.version;
  if (values.recordedAt) payload.recorded_at = values.recordedAt;
  if (values.note) payload.note = values.note;

  const { data } = await api.post(`/business/clients/${id}/consents`, payload);
  return data.data.consent;
};

/* -------------------------------------------------------------------- merge */

/** GET the records that look like duplicates of this one — exact matches only. */
export const fetchDuplicates = async (id) => {
  const { data } = await api.get(`/business/clients/${id}/duplicates`);
  return data.data.clients;
};

/**
 * POST /business/clients/{winner}/merge
 *
 * The client in the path survives; the loser is folded in and soft-deleted.
 * The survivor's own fields are never overwritten — a merge only fills blanks.
 */
export const mergeClients = async (winnerId, loserId) => {
  const { data } = await api.post(`/business/clients/${winnerId}/merge`, { loser_id: loserId });
  return data.data.client;
};
